import { RefObject, useEffect, useRef } from 'react';

type UseSelectedSlotAutoScrollArgs = {
	containerRef: RefObject<HTMLElement>;
	selectedSlotId: string | null;
	enabled: boolean;
	deps?: unknown[];
};

export function useSelectedSlotAutoScroll({
	containerRef,
	selectedSlotId,
	enabled,
	deps = [],
}: UseSelectedSlotAutoScrollArgs) {
	const autoScrolledSlotIdRef = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || !selectedSlotId) {
			autoScrolledSlotIdRef.current = null;
			return;
		}
		if (autoScrolledSlotIdRef.current === selectedSlotId) return;

		let frameId = 0;
		let attempts = 0;
		const maxAttempts = 120;

		const tryScroll = () => {
			if (autoScrolledSlotIdRef.current === selectedSlotId) return;

			const container = containerRef.current;
			if (!container || container.clientHeight === 0) {
				if (attempts < maxAttempts) {
					attempts += 1;
					frameId = requestAnimationFrame(tryScroll);
				}
				return;
			}

			const escapedSlotId = typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
				? CSS.escape(selectedSlotId)
				: selectedSlotId.replace(/"/g, '\\"');
			const selectedElement = container.querySelector<HTMLElement>(`[data-slot-id="${escapedSlotId}"]`);

			if (!selectedElement) {
				if (attempts < maxAttempts) {
					attempts += 1;
					frameId = requestAnimationFrame(tryScroll);
				}
				return;
			}

			selectedElement.scrollIntoView({
				behavior: 'auto',
				block: 'center',
				inline: 'nearest',
			});

			autoScrolledSlotIdRef.current = selectedSlotId;
		};

		frameId = requestAnimationFrame(tryScroll);

		return () => {
			if (frameId) cancelAnimationFrame(frameId);
		};
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [containerRef, enabled, selectedSlotId, ...deps]);
}